import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { AuszugInfoItem, AuszugModel, AuszugPrintService, FormatEnum } from './auszug-print-service';
import { RechteAuszugSettingsComponent } from './settings/rechte-auszug-settings.component';
import { EigentuemerAuszugSettingsComponent } from './settings/eigentuemer-auszug-settings.component';
import { GrundstueckAuszugSettingsComponent } from './settings/grundstueck-auszug-settings.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AuszugPrintOptions, AuszugSettingsService } from './settings/auszug-settings-service';
import { GbPrintService } from '../../../core/services/app-print.service';
import { NgTemplateOutlet } from '@angular/common';

@Component({
  selector: 'cap-auszug',
  templateUrl: './auszug.component.html',
  styleUrls: ['./auszug.component.scss'],
  imports: [NgbTooltip, NgTemplateOutlet, TranslatePipe]
})
export class AuszugComponent implements OnInit, OnDestroy {
  private initialized = false;

  _item: AuszugModel;
  _indexNumber: number;
  _rechtVersionId: string;

  @Input()
  withoutFormat: boolean;

  @Input()
  set item(model: AuszugModel) {
    this._item = model;
    if (this.initialized) {
      this.reload();
    }
  }

  @Input()
  set indexNumber(indexNumber: number) {
    this._indexNumber = indexNumber;
    if (this.initialized) {
      this.reload();
    }
  }

  @Input()
  set rechtVersionId(model: string) {
    this._rechtVersionId = model;
    if (this.initialized) {
      this.reload();
    }
  }

  auszugItem: AuszugInfoItem;
  private subscription: Subscription;

  constructor(
    private auszugService: AuszugPrintService,
    private modalService: NgbModal,
    private translate: TranslateService,
    private auszugSettingsService: AuszugSettingsService,
    public gbPrintService: GbPrintService
  ) {
    keysToTranslate(['AuszugZuRechtErstellen', 'AuszugZuGrundstueckErstellen', 'AuszugZuEigentuemerErstellen', 'Eigentümerauszug']);
  }

  ngOnInit(): void {
    this.subscription = this.translate.onLangChange.subscribe(
      () => (this.auszugItem = this.auszugService.createAuszugFrom(this._item, this._rechtVersionId))
    );
    this.reload();
  }

  private reload() {
    this.auszugItem = this.auszugService.createAuszugFrom(this._item, this._rechtVersionId);
    this.initialized = true;
  }

  selectSettings(format: FormatEnum) {
    switch (this.auszugItem.type) {
      case 'Recht':
        this.selectRechtSettings(format);
        break;
      case 'Grundstueck':
        this.selectGrundstueckSettings(format);
        break;
      case 'Eigentuemer':
        this.selectEigentuemerSettings(format);
        break;
      case 'Grundbuchmeldung':
        this.selectMeldungSettings(format);
        break;
      default:
        throw new Error(this.auszugItem.type + ' is not supported');
    }
  }

  selectRechtSettings(format: FormatEnum) {
    const modalRef = this.modalService.open(RechteAuszugSettingsComponent);
    modalRef.componentInstance.format = format;
    modalRef.componentInstance.title = this.auszugItem.settingsTitle;
    modalRef.componentInstance.auszugItem = this.auszugItem;
    modalRef.componentInstance.indexNumber = this._indexNumber;
  }

  selectGrundstueckSettings(format: FormatEnum) {
    const modalRef = this.modalService.open(GrundstueckAuszugSettingsComponent);
    modalRef.componentInstance.format = format;
    modalRef.componentInstance.title = this.auszugItem.settingsTitle;
    modalRef.componentInstance.auszugItem = this.auszugItem;
    modalRef.componentInstance.indexNumber = this._indexNumber;
  }

  selectEigentuemerSettings(format: FormatEnum) {
    const modalRef = this.modalService.open(EigentuemerAuszugSettingsComponent);
    modalRef.componentInstance.format = format;
    modalRef.componentInstance.title = this.auszugItem.settingsTitle;
    modalRef.componentInstance.auszugItem = this.auszugItem;
    if (!format && this.auszugItem.printPDFPossible && this.auszugItem.printRTFPossible) {
      modalRef.componentInstance.showFormatSelection = true;
    } else if (!format && this.auszugItem.printPDFPossible) {
      modalRef.componentInstance.format = 'PDF';
    } else if (!format && this.auszugItem.printRTFPossible) {
      modalRef.componentInstance.format = 'RTF';
    }
  }

  selectMeldungSettings(format: FormatEnum) {
    const auszugPrintOption = <AuszugPrintOptions>{
      auszugType: 'Grundbuchmeldung',
      printOptions: null,
      format: format,
      item: this.auszugItem,
      indexNumber: this._indexNumber
    };
    this.auszugSettingsService.confirmPrintSelection(auszugPrintOption);
  }

  isPrintingInProgress() {
    return this.gbPrintService.printInProgressMap.size > 0;
  }

  showSpinner(indexNumber: number, format: string) {
    if (this.gbPrintService.printInProgressMap.has(indexNumber)) {
      return this.gbPrintService.printInProgressMap.get(indexNumber) === format;
    }
    return false;
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
